import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import Card from '../../../components/ui/Card'
import SearchableSelect from '../../../components/ui/SearchableSelect'
import Badge from '../../../components/ui/Badge'
import { Pagination } from '../../../components/ui/Table'
import api from '../../../lib/api'
import { formatDate } from '../../../lib/formatters'

// API response types
interface AuditLogResponse {
  id: string
  user_id?: string
  user_name?: string
  user_email?: string
  action: string
  entity_type: string
  entity_id: string
  summary: string
  created_at: string
}

interface AuditLogDisplay {
  id: string
  action: string
  entityType: string
  entityId: string
  user?: { name: string; email: string }
  summary: string
  createdAt: string
}

const ACTION_COLORS: Record<string, 'success' | 'info' | 'warning' | 'error' | 'gray'> = {
  create: 'success',
  update: 'info',
  delete: 'error',
  cancel: 'error',
  duplicate: 'warning',
}

const ENTITY_ICONS: Record<string, string> = {
  doctor: 'fa-user-doctor',
  work_order: 'fa-clipboard-list',
  work_item: 'fa-tags',
  invoice: 'fa-file-invoice',
  payment: 'fa-credit-card',
}

export default function AuditLogPage() {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [entityType, setEntityType] = useState('')
  const [action, setAction] = useState('')

  interface ApiResponse {
    data: AuditLogResponse[]
    meta: {
      page: number
      per_page: number
      total: number
      total_pages: number
    }
  }

  const { data, isLoading } = useQuery({
    queryKey: ['audit-logs', page, search, entityType, action],
    queryFn: async () => {
      try {
        const params = new URLSearchParams({ page: String(page), per_page: '20' })
        if (search) params.append('search', search)
        if (entityType) params.append('entity_type', entityType)
        if (action) params.append('action', action)
        const { data } = await api.get<ApiResponse>(`/audit-logs?${params}`)

        const logs: AuditLogDisplay[] = (data.data || []).map(log => ({
          id: log.id,
          action: log.action,
          entityType: log.entity_type,
          entityId: log.entity_id,
          user: log.user_name ? { name: log.user_name, email: log.user_email || '' } : undefined,
          summary: log.summary,
          createdAt: log.created_at,
        }))

        return {
          data: logs,
          total: data.meta?.total || 0,
          totalPages: data.meta?.total_pages || 1,
        }
      } catch (err) {
        console.error('Failed to fetch audit logs:', err)
        return { data: [], total: 0, totalPages: 1 }
      }
    },
  })

  const columns = [
    {
      key: 'timestamp',
      header: 'When',
      render: (log: AuditLogDisplay) => (
        <div className="text-sm">
          <p className="text-gray-900 dark:text-white">{formatDate(log.createdAt)}</p>
          <p className="text-gray-500 text-xs">
            {new Date(log.createdAt).toLocaleTimeString()}
          </p>
        </div>
      ),
    },
    {
      key: 'user',
      header: 'User',
      render: (log: AuditLogDisplay) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
            <i className="fas fa-user text-gray-500 text-xs"></i>
          </div>
          <div>
            <p className="font-medium text-gray-900 dark:text-white text-sm">
              {log.user?.name ?? 'System'}
            </p>
            <p className="text-xs text-gray-500">{log.user?.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'action',
      header: 'Action',
      render: (log: AuditLogDisplay) => (
        <Badge variant={ACTION_COLORS[log.action] || 'gray'}>
          {log.action}
        </Badge>
      ),
    },
    {
      key: 'entity',
      header: 'Entity',
      render: (log: AuditLogDisplay) => (
        <div className="flex items-center gap-2">
          <i className={`fas ${ENTITY_ICONS[log.entityType] || 'fa-cube'} text-gray-400`}></i>
          <span className="text-sm text-gray-900 dark:text-white capitalize">
            {log.entityType.replace('_', ' ')}
          </span>
        </div>
      ),
    },
    {
      key: 'summary',
      header: 'Details',
      render: (log: AuditLogDisplay) => (
        <p className="text-sm text-gray-700 dark:text-gray-300 max-w-md truncate" title={log.summary}>
          {log.summary}
        </p>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Activity Log</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Track all changes and actions in your system
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
              <i className="fas fa-list text-blue-600"></i>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{data?.total ?? 0}</p>
              <p className="text-xs text-gray-500">Total Events</p>
            </div>
          </div>
        </Card>
        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
              <i className="fas fa-plus text-green-600"></i>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {data?.data?.filter(l => l.action === 'create').length ?? 0}
              </p>
              <p className="text-xs text-gray-500">Created</p>
            </div>
          </div>
        </Card>
        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center">
              <i className="fas fa-edit text-amber-600"></i>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {data?.data?.filter(l => l.action === 'update').length ?? 0}
              </p>
              <p className="text-xs text-gray-500">Updated</p>
            </div>
          </div>
        </Card>
        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
              <i className="fas fa-trash text-red-600"></i>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {data?.data?.filter(l => l.action === 'delete').length ?? 0}
              </p>
              <p className="text-xs text-gray-500">Deleted</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search activities..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1) }}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <SearchableSelect
            value={entityType}
            onChange={(val) => { setEntityType(val); setPage(1) }}
            placeholder="All Entities"
            searchable={false}
            options={[
              { value: '', label: 'All Entities' },
              { value: 'work_order', label: 'Work Orders' },
              { value: 'invoice', label: 'Invoices' },
              { value: 'payment', label: 'Payments' },
              { value: 'work_item', label: 'Catalog Items' },
              { value: 'doctor', label: 'Doctors' },
            ]}
          />
          <SearchableSelect
            value={action}
            onChange={(val) => { setAction(val); setPage(1) }}
            placeholder="All Actions"
            searchable={false}
            options={[
              { value: '', label: 'All Actions' },
              { value: 'create', label: 'Create' },
              { value: 'update', label: 'Update' },
              { value: 'delete', label: 'Delete' },
              { value: 'duplicate', label: 'Duplicate' },
              { value: 'cancel', label: 'Cancel' },
            ]}
          />
        </div>
      </Card>

      {/* Table */}
      <Card padding="none">
        <div className="overflow-x-auto">
          <table className="table w-full">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col.key}>{col.header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={columns.length} className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                  </td>
                </tr>
              ) : (data?.data ?? []).length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="text-center py-8 text-gray-500">
                    No activity logs found
                  </td>
                </tr>
              ) : (
                (data?.data ?? []).map((log) => (
                  <tr key={log.id}>
                    {columns.map((col) => (
                      <td key={col.key}>{col.render(log)}</td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {data && data.totalPages > 1 && (
          <Pagination
            page={page}
            totalPages={data.totalPages}
            onPageChange={setPage}
          />
        )}
      </Card>

      {/* Retention notice */}
      <p className="text-xs text-gray-500 text-center">
        Activity logs are retained for 90 days
      </p>
    </div>
  )
}
